import { appDataType, appTheme } from '../types/types';
import { clearPatientOnboardingSession } from './patientOnboardingSelection';

const APP_DATA_STORAGE_KEY = 'appData';

const WIZARD_FIELD_DEFAULTS = {
  ratio: 0,
  distance: '',
  footSteps: 0,
  customSize: '',
  footSizeUnit: '',
};

const APP_PREFS_DEFAULTS = {
  language: 'en',
  unit: 'meter',
  theme: 'default' as appTheme,
};

type PatientWizardPrefs = Pick<appDataType, 'language' | 'theme' | 'unit'>;

// Returns default appData with wizard fields cleared and optional language, theme, and unit preserved.
export function getPatientWizardInitialAppData(preserve?: Partial<PatientWizardPrefs>): appDataType {
  return {
    ...APP_PREFS_DEFAULTS,
    ...WIZARD_FIELD_DEFAULTS,
    ...preserve,
  };
}

// Reads persisted appData from localStorage or sessionStorage for integration flows.
function readStoredAppData(isIntegration: boolean): appDataType | null {
  const raw = isIntegration ? sessionStorage.getItem(APP_DATA_STORAGE_KEY) : localStorage.getItem(APP_DATA_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as appDataType;
  } catch {
    return null;
  }
}

// Clears wizard fields in storage and returns the reset appData object.
export function resetPatientWizardStorage(options: {
  isIntegration: boolean;
  preserve?: Partial<PatientWizardPrefs>;
}): appDataType {
  clearPatientOnboardingSession();

  const stored = readStoredAppData(options.isIntegration);
  const preserve: PatientWizardPrefs = {
    language: options.preserve?.language ?? stored?.language ?? APP_PREFS_DEFAULTS.language,
    theme: options.preserve?.theme ?? stored?.theme ?? APP_PREFS_DEFAULTS.theme,
    unit: options.preserve?.unit ?? stored?.unit ?? APP_PREFS_DEFAULTS.unit,
  };

  const resetData = getPatientWizardInitialAppData(preserve);
  const storage = options.isIntegration ? sessionStorage : localStorage;
  storage.setItem(APP_DATA_STORAGE_KEY, JSON.stringify(resetData));

  return resetData;
}
